import { useEffect, useState } from 'react'
import { searchParam } from '../router.js'

/**
 * Which sub-tab a screen opens on, in this order: the one a link asked for (`?tab=census`, when it is one the person
 * has), else the one this browser last chose there (localStorage, a convenience only), else the first. A link into the
 * screen while it is already open still lands on its tab, and every choice is remembered. `openOnFirst` skips the
 * remembered one when the screen mounts, so it opens on the first tab (or the one a link asked for).
 */
export function useRememberedTab<Tab extends string>(storageKey: string, available: readonly Tab[], { openOnFirst = false }: { openOnFirst?: boolean } = {}): { current: Tab; choose: (tab: Tab) => void } {
  const [active, setActive] = useState<Tab>(() => (openOnFirst ? (requestedTab(available) ?? (available[0] as Tab)) : rememberedTab(storageKey, available)))

  useEffect(() => {
    const follow = (): void => {
      const asked = requestedTab(available)
      if (asked) {
        setActive(asked)
      }
    }
    window.addEventListener('popstate', follow)
    return () => window.removeEventListener('popstate', follow)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `available` only changes with the role, which remounts the shell.
  }, [])

  const current = available.includes(active) ? active : (available[0] as Tab)
  const choose = (tab: Tab): void => {
    setActive(tab)
    try {
      window.localStorage.setItem(storageKey, tab)
    } catch {
      // A blocked store only means the choice is not remembered.
    }
  }
  return { current, choose }
}

/** The tab a link asked for, when it is one the person has. */
function requestedTab<Tab extends string>(available: readonly Tab[]): Tab | null {
  const asked = searchParam('tab') as Tab | null
  return asked && available.includes(asked) ? asked : null
}

function rememberedTab<Tab extends string>(storageKey: string, available: readonly Tab[]): Tab {
  const asked = requestedTab(available)
  if (asked) {
    return asked
  }
  try {
    const stored = window.localStorage.getItem(storageKey) as Tab | null
    return stored && available.includes(stored) ? stored : (available[0] as Tab)
  } catch {
    return available[0] as Tab
  }
}
